// Pure board-derived memos of Scene: the node partition, the sea layer, the
// camera centre, the particle bounds and the snow peaks. Each is exactly the
// `useMemo` it was inline in Scene.tsx; Scene calls them at the same positions
// in the same order, so the hook sequence is unchanged.

import type { GameSnapshot, SceneNode, SeaState } from '@aod/protocol';
import { RIM_SEA } from '@aod/protocol';
import { useMemo } from 'react';
import type { SnowPeak } from '../MountainSnow.js';
import { mountainClusterBoost, terrainLevelsActive, tileSurfaceY } from '../elevation.js';
import { extraOceanRings, frameForMode } from '../sea/seaFrame.js';
import { protoSeaRings, seaProto } from '../sea/seaProto.js';
import { applyLandlockedWall } from '../sea/seaTiles.js';

/** Partition the snapshot's scene children into tiles, cities and units. */
export function useSceneNodes(snapshot: GameSnapshot) {
  return useMemo(() => {
    const all = snapshot.scene.children ?? [];
    const tiles: SceneNode[] = [];
    const cities: SceneNode[] = [];
    const units: SceneNode[] = [];
    for (const node of all) {
      if (node.type === 'tile') tiles.push(node);
      else if (node.type === 'city') cities.push(node);
      else if (node.type === 'unit') units.push(node);
    }
    return { tiles, cities, units };
  }, [snapshot]);
}

/**
 * The sea axis the sim generated the board with (sea-design §3 A2), the tiles
 * the board renders (the DEV `?seaProto=…` landlocked wall applied), the sea
 * layer to mount, and how far past the board the camera may reach.
 */
export function useSeaFrame(simTiles: SceneNode[], sea: SeaState | undefined) {
  // The snapshot publishes it; a board without it is today's rim board.
  const seaState = sea ?? RIM_SEA;
  // DEV preview of the sea frame (?seaProto=…, default OFF => `tiles` IS
  // `simTiles`). A real landlocked match already has its wall from the sim,
  // so the preview stands down when the sim says the board is not a rim.
  const proto = seaProto();
  const preview = proto && seaState.mode === 'rim' ? proto : null;
  const tiles = useMemo(
    () =>
      preview?.mode === 'landlocked' ? (applyLandlockedWall(simTiles) as SceneNode[]) : simTiles,
    [simTiles, preview],
  );
  // The sea layer. A landlocked board gets the mist curtain (the void
  // beyond the wall is fog, not sea); an open board's ocean is real tiles,
  // so it needs no layer; the rim board has none outside the DEV preview.
  const landlocked = seaState.mode === 'landlocked';
  const seaLayer = useMemo(() => {
    if (landlocked) return { mode: seaState.mode, frame: seaState.frame };
    if (preview) {
      return { mode: preview.mode, frame: frameForMode(preview.mode, preview.width) };
    }
    return null;
  }, [landlocked, seaState.mode, seaState.frame, preview]);
  // How far past the board the camera may reach: the open sea's extra
  // ocean rings, or a ring of slack for the landlocked mist curtain.
  const seaRings = preview
    ? protoSeaRings(preview)
    : landlocked
      ? 1
      : extraOceanRings(seaState.frame);
  return { tiles, landlocked, seaLayer, seaRings };
}

/** Centre camera on the centroid of the map for a good default framing. */
export function useBoardCenter(tiles: SceneNode[]) {
  return useMemo<[number, number, number]>(() => {
    if (tiles.length === 0) return [0, 0, 0];
    let sx = 0;
    let sz = 0;
    for (const t of tiles) {
      sx += t.transform.position[0];
      sz += t.transform.position[2];
    }
    return [sx / tiles.length, 0, sz / tiles.length];
  }, [tiles]);
}

/** Particle spawn bounds from tile extents. */
export function useParticleBounds(tiles: SceneNode[]) {
  return useMemo<[number, number, number, number] | null>(() => {
    if (tiles.length === 0) return null;
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    for (const t of tiles) {
      const x = t.transform.position[0];
      const z = t.transform.position[2];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    const pad = 1;
    return [minX - pad, maxX + pad, minZ - pad, maxZ + pad];
  }, [tiles]);
}

/**
 * Massif-core peaks (mountain tiles boosted by 3+ mountain neighbours):
 * MountainSnow flurries fall over these.
 */
export function useSnowPeaks(tiles: SceneNode[], biomeMap: Parameters<typeof tileSurfaceY>[2]) {
  return useMemo<SnowPeak[]>(() => {
    const out: SnowPeak[] = [];
    for (const tile of tiles) {
      const props = tile.props as Record<string, unknown> | undefined;
      const q = props?.q as number | undefined;
      const r = props?.r as number | undefined;
      if (q === undefined || r === undefined) continue;
      if (props?.biome !== 'mountain') continue;
      // Levelled board: snow-line peaks are the high ground (level >= 9);
      // otherwise the massif-core heuristic.
      if (terrainLevelsActive()) {
        if (((props?.level as number | undefined) ?? 0) < 9) continue;
      } else if (mountainClusterBoost(q, r, biomeMap) <= 0) continue;
      const [x, , z] = tile.transform.position;
      out.push({ x, y: tileSurfaceY(q, r, biomeMap), z });
    }
    return out;
  }, [tiles, biomeMap]);
}
